/**
 * AssignmentBoardPage - Hierarchical view of Projects → Shifts → Tasks → Crew
 * PROMPT 42: Assignment Board (Project / Shift / Crew View)
 * PROMPT 44: UX Upgrade - uTrac-style operational view with compact/expanded modes
 * PROMPT 45: Drag-and-Drop Crew Assignment with split-pane layout
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  closestCenter,
} from "@dnd-kit/core";
import {
  getAssignmentBoard,
  getAssignmentBoardProjects,
  getEligibleCrew,
  assignCrewToTask,
  removeCrewFromTask,
  sendAssignmentReminder,
  getStaffList,
  extractErrorMessage,
} from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Users,
  Calendar,
  MapPin,
  Clock,
  UserPlus,
  Mail,
  MoreVertical,
  Loader2,
  AlertTriangle,
  Trash2,
  LayoutList,
  LayoutGrid,
  Minus,
  Plus,
  User,
  GripVertical,
  Filter,
  FilterX,
  X,
  Hand,
  Ban,
  Check,
  BellRing,
} from "lucide-react";

// ============================================================================
// STATUS INDICATOR COMPONENTS
// ============================================================================

function StatusIndicator({ status, seenAt, size = "default" }) {
  const sizeClasses = size === "compact" 
    ? "w-4 h-4 text-[10px]" 
    : "w-5 h-5 text-xs";
  
  if (status === "accepted") {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={`inline-flex items-center justify-center rounded font-bold bg-green-900/50 text-green-300 border border-green-700 ${sizeClasses}`}
              data-testid="status-accepted"
            >
              ✓
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">Accepted</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  if (status === "rejected") {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={`inline-flex items-center justify-center rounded font-bold bg-red-900/50 text-red-300 border border-red-700 ${sizeClasses}`}
              data-testid="status-rejected"
            >
              ✕
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">Rejected</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  if (seenAt) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={`inline-flex items-center justify-center rounded font-bold bg-yellow-900/50 text-yellow-300 border border-yellow-700 ${sizeClasses}`}
              data-testid="status-seen"
            >
              ?
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">Seen (awaiting response)</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center justify-center rounded font-bold bg-blue-900/50 text-blue-300 border border-blue-700 ${sizeClasses}`}
            data-testid="status-sent"
          >
            S
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">Sent (not yet viewed)</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function CapacityIndicator({ required, assigned, accepted, compact = false }) {
  const isUnderstaffed = assigned < required;
  const allAccepted = accepted === assigned && assigned >= required;
  const fillPercent = required > 0 ? Math.min((assigned / required) * 100, 100) : 0;
  
  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                allAccepted
                  ? "bg-green-900/40 text-green-300 border border-green-700/50"
                  : isUnderstaffed
                  ? "bg-red-900/40 text-red-300 border border-red-700/50"
                  : "bg-surface-elevated/50 text-foreground border border-border/50"
              }`}
              data-testid="capacity-indicator-compact"
            >
              <span className={isUnderstaffed ? "text-red-400" : ""}>{assigned}</span>
              <span className="text-muted-foreground">/</span>
              <span>{required}</span>
              {isUnderstaffed && <AlertTriangle className="h-2.5 w-2.5 text-red-400" />}
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            Required: {required} | Assigned: {assigned} | Accepted: {accepted}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2" data-testid="capacity-indicator">
            <div className="w-16 h-2 bg-surface-elevated rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${
                  isUnderstaffed ? "bg-red-500" : allAccepted ? "bg-green-500" : "bg-teal-500"
                }`}
                style={{ width: `${fillPercent}%` }}
              />
            </div>
            <div className={`flex items-center gap-0.5 text-xs font-medium ${
              isUnderstaffed ? "text-red-400" : allAccepted ? "text-green-400" : "text-foreground"
            }`}>
              <span>{assigned}</span>
              <span className="text-muted-foreground">/</span>
              <span>{required}</span>
              {accepted > 0 && (
                <>
                  <span className="text-muted-foreground ml-1">(</span>
                  <span className="text-green-400">{accepted}✓</span>
                  <span className="text-muted-foreground">)</span>
                </>
              )}
              {isUnderstaffed && <AlertTriangle className="h-3 w-3 text-red-400 ml-1" />}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          <div className="space-y-1">
            <p>Required: {required}</p>
            <p>Assigned: {assigned}</p>
            <p>Accepted: {accepted}</p>
            {isUnderstaffed && <p className="text-red-400">⚠ Understaffed by {required - assigned}</p>}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ============================================================================
// DRAG-AND-DROP CREW POOL (LEFT PANEL)
// ============================================================================

/**
 * Draggable crew card for the crew pool
 */
function DraggableCrewCard({ staff, isAssigned, isDragging, isQualified, selectedTask }) {
  const isDisabled = isAssigned || (selectedTask && !isQualified);
  
  return (
    <div
      className={`
        flex items-center gap-2 p-2 rounded-lg border transition-all
        ${isDragging ? "opacity-50 scale-95" : ""}
        ${isDisabled 
          ? "bg-surface-elevated/30 border-border/50 opacity-50 cursor-not-allowed" 
          : "bg-surface-elevated/50 border-border hover:border-teal-600 cursor-grab active:cursor-grabbing"
        }
      `}
      data-testid={`crew-card-${staff.id}`}
    >
      {!isDisabled && (
        <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      )}
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <User className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <span className="text-sm font-medium text-white truncate">
            {staff.first_name} {staff.last_name}
          </span>
        </div>
        
        {staff.skills && staff.skills.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {staff.skills.slice(0, 3).map((skill) => (
              <Badge
                key={skill}
                variant="outline"
                className={`text-[9px] px-1 py-0 ${
                  selectedTask && selectedTask.required_skills?.includes(skill)
                    ? "border-green-600 text-green-400 bg-green-900/20"
                    : "border-border text-muted-foreground"
                }`}
              >
                {skill}
              </Badge>
            ))}
            {staff.skills.length > 3 && (
              <span className="text-[9px] text-muted-foreground">+{staff.skills.length - 3}</span>
            )}
          </div>
        )}
      </div>
      
      {isAssigned && (
        <Badge variant="outline" className="text-[9px] border-amber-600 text-amber-400 flex-shrink-0">
          Assigned
        </Badge>
      )}
      
      {selectedTask && !isQualified && !isAssigned && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Ban className="h-4 w-4 text-red-400 flex-shrink-0" />
            </TooltipTrigger>
            <TooltipContent side="left" className="text-xs max-w-[200px]">
              Missing required skills for selected task
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

/**
 * Crew Pool Panel (Left Side)
 */
function CrewPoolPanel({
  allCrew,
  assignedStaffIds,
  selectedTask,
  filterByTask,
  setFilterByTask,
  loading,
  onDragStart,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});
  
  // Group crew by skills
  const groupedCrew = useMemo(() => {
    const groups = { "No Skills": [] };
    
    allCrew.forEach((staff) => {
      const skills = staff.skills || [];
      if (skills.length === 0) {
        groups["No Skills"].push(staff);
      } else {
        // Add to each skill group
        skills.forEach((skill) => {
          if (!groups[skill]) groups[skill] = [];
          if (!groups[skill].find((s) => s.id === staff.id)) {
            groups[skill].push(staff);
          }
        });
      }
    });
    
    // Remove empty groups
    Object.keys(groups).forEach((key) => {
      if (groups[key].length === 0) delete groups[key];
    });
    
    return groups;
  }, [allCrew]);
  
  // Check if staff is qualified for selected task
  const isQualifiedForTask = useCallback((staff) => {
    if (!selectedTask || !selectedTask.required_skills || selectedTask.required_skills.length === 0) {
      return true;
    }
    const staffSkills = new Set(staff.skills || []);
    return selectedTask.required_skills.every((skill) => staffSkills.has(skill));
  }, [selectedTask]);
  
  // Filter crew based on search and task filter
  const filteredCrew = useMemo(() => {
    let result = allCrew;
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((staff) =>
        `${staff.first_name} ${staff.last_name}`.toLowerCase().includes(term) ||
        staff.email?.toLowerCase().includes(term) ||
        staff.skills?.some((s) => s.toLowerCase().includes(term))
      );
    }
    
    // Filter by task requirements if enabled
    if (filterByTask && selectedTask) {
      result = result.filter((staff) => isQualifiedForTask(staff));
    }
    
    return result;
  }, [allCrew, searchTerm, filterByTask, selectedTask, isQualifiedForTask]);
  
  // Re-group filtered crew
  const filteredGroupedCrew = useMemo(() => {
    const groups = { "All Crew": filteredCrew };
    return groups;
  }, [filteredCrew]);
  
  const toggleGroup = (group) => {
    setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };
  
  return (
    <div className="w-72 flex-shrink-0 bg-surface-base/50 border-r border-border flex flex-col h-full">
      {/* Header */}
      <div className="p-3 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-white flex items-center gap-2">
            <Users className="h-4 w-4 text-teal-400" />
            Crew Pool
          </h3>
          <span className="text-xs text-muted-foreground">{filteredCrew.length} available</span>
        </div>
        
        {/* Search */}
        <input
          type="text"
          placeholder="Search crew..."
          className="w-full px-3 py-1.5 text-sm bg-surface-elevated border border-border rounded text-white placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-teal-500"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        
        {/* Filter Toggle */}
        {selectedTask && (
          <div className="mt-2">
            <button
              onClick={() => setFilterByTask(!filterByTask)}
              className={`w-full flex items-center justify-center gap-2 px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                filterByTask
                  ? "bg-teal-600 text-white"
                  : "bg-surface-elevated text-foreground hover:bg-surface-hover"
              }`}
            >
              {filterByTask ? (
                <>
                  <Filter className="h-3 w-3" />
                  Filtered by Task Requirements
                </>
              ) : (
                <>
                  <FilterX className="h-3 w-3" />
                  Show All Crew
                </>
              )}
            </button>
            
            {filterByTask && selectedTask.required_skills?.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {selectedTask.required_skills.map((skill) => (
                  <Badge key={skill} className="text-[9px] bg-teal-900/50 text-teal-300 border-teal-700">
                    {skill}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Crew List */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : filteredCrew.length === 0 ? (
          <div className="text-center py-8">
            <Users className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No crew available</p>
            {filterByTask && (
              <button
                onClick={() => setFilterByTask(false)}
                className="mt-2 text-xs text-teal-400 hover:text-teal-300"
              >
                Show all crew
              </button>
            )}
          </div>
        ) : (
          filteredCrew.map((staff) => {
            const isAssigned = assignedStaffIds.has(staff.id);
            const isQualified = isQualifiedForTask(staff);
            
            return (
              <div
                key={staff.id}
                draggable={!isAssigned && (!selectedTask || isQualified)}
                onDragStart={(e) => {
                  if (isAssigned || (selectedTask && !isQualified)) {
                    e.preventDefault();
                    return;
                  }
                  onDragStart(staff);
                  e.dataTransfer.setData("text/plain", JSON.stringify(staff));
                  e.dataTransfer.effectAllowed = "copy";
                }}
              >
                <DraggableCrewCard
                  staff={staff}
                  isAssigned={isAssigned}
                  isQualified={isQualified}
                  selectedTask={selectedTask}
                />
              </div>
            );
          })
        )}
      </div>
      
      {/* Footer hint */}
      <div className="p-2 border-t border-border text-center">
        <p className="text-[10px] text-muted-foreground">
          <Hand className="h-3 w-3 inline mr-1" />
          Drag crew to assign to tasks
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// DROPPABLE TASK COMPONENTS
// ============================================================================

/**
 * Assigned crew member in a task (with removal options)
 */
function AssignedCrewMember({ crew, taskId, onRemove, onSendReminder }) {
  return (
    <div
      className="flex items-center gap-2 py-1.5 px-2 bg-surface-elevated/50 rounded border border-border group"
      data-testid={`assigned-crew-${crew.staff_id}`}
    >
      <StatusIndicator status={crew.status} seenAt={crew.seen_at} size="compact" />
      <User className="h-3 w-3 text-muted-foreground" />
      <span className="text-sm text-foreground flex-1 truncate">{crew.staff_name}</span>
      
      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {crew.status === "pending" && (
          <button
            className="p-1 text-amber-400 hover:text-amber-300 hover:bg-amber-900/30 rounded"
            onClick={() => onSendReminder(taskId, crew.staff_id)}
            title="Send reminder"
          >
            <Mail className="h-3 w-3" />
          </button>
        )}
        <button
          className="p-1 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded"
          onClick={() => onRemove(taskId, crew)}
          title="Remove from task"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

/**
 * Droppable task zone for drag-and-drop
 */
function DroppableTaskZone({
  task,
  isSelected,
  onSelect,
  onDrop,
  onRemoveCrew,
  onSendReminder,
  isDragOver,
  draggedStaff,
}) {
  const isUnderstaffed = task.assigned_count < task.required_staff;
  const hasRejected = task.crew?.some((c) => c.status === "rejected");
  
  // Check if dragged staff is qualified
  const isQualifiedDrop = useMemo(() => {
    if (!draggedStaff) return true;
    if (!task.required_skills || task.required_skills.length === 0) return true;
    const staffSkills = new Set(draggedStaff.skills || []);
    return task.required_skills.every((skill) => staffSkills.has(skill));
  }, [draggedStaff, task.required_skills]);
  
  // Check if dragged staff is already assigned
  const isAlreadyAssigned = useMemo(() => {
    if (!draggedStaff) return false;
    return task.crew?.some((c) => c.staff_id === draggedStaff.id);
  }, [draggedStaff, task.crew]);
  
  const canDrop = isQualifiedDrop && !isAlreadyAssigned;
  
  return (
    <div
      className={`
        rounded-lg border-2 transition-all p-2
        ${isSelected ? "border-teal-500 bg-teal-900/10" : "border-transparent"}
        ${isDragOver && canDrop ? "border-teal-400 bg-teal-900/20 border-dashed" : ""}
        ${isDragOver && !canDrop ? "border-red-400 bg-red-900/20 border-dashed" : ""}
        ${isUnderstaffed ? "bg-red-900/5" : ""}
      `}
      onClick={() => onSelect(task)}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = canDrop ? "copy" : "none";
      }}
      onDrop={(e) => {
        e.preventDefault();
        if (!canDrop) {
          if (!isQualifiedDrop) {
            toast.error("Crew member is not qualified for this task");
          } else if (isAlreadyAssigned) {
            toast.error("Crew member is already assigned to this task");
          }
          return;
        }
        try {
          const staff = JSON.parse(e.dataTransfer.getData("text/plain"));
          onDrop(task, staff);
        } catch (err) {
          console.error("Drop error:", err);
        }
      }}
      data-testid={`droppable-task-${task.task_id}`}
    >
      {/* Task Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-white">{task.task_name}</span>
          {isSelected && (
            <Badge className="text-[9px] bg-teal-600 text-white">Selected</Badge>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <CapacityIndicator
            required={task.required_staff}
            assigned={task.assigned_count}
            accepted={task.accepted_count}
            compact
          />
        </div>
      </div>
      
      {/* Required Skills */}
      {task.required_skills && task.required_skills.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          <span className="text-[9px] text-muted-foreground mr-1">Requires:</span>
          {task.required_skills.map((skill) => (
            <Badge
              key={skill}
              variant="outline"
              className="text-[9px] px-1 py-0 border-border text-muted-foreground"
            >
              {skill}
            </Badge>
          ))}
        </div>
      )}
      
      {/* Warnings */}
      {(isUnderstaffed || hasRejected) && (
        <div className="flex flex-wrap gap-2 mb-2">
          {isUnderstaffed && (
            <span className="text-[9px] text-red-400 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              Understaffed by {task.required_staff - task.assigned_count}
            </span>
          )}
          {hasRejected && (
            <span className="text-[9px] text-amber-400 flex items-center gap-1">
              <X className="h-3 w-3" />
              Has rejected assignments
            </span>
          )}
        </div>
      )}
      
      {/* Assigned Crew */}
      <div className="space-y-1">
        {task.crew && task.crew.length > 0 ? (
          task.crew.map((crew) => (
            <AssignedCrewMember
              key={crew.assignment_id}
              crew={crew}
              taskId={task.task_id}
              onRemove={onRemoveCrew}
              onSendReminder={onSendReminder}
            />
          ))
        ) : (
          <div className="py-3 text-center border-2 border-dashed border-border rounded text-muted-foreground text-xs">
            <UserPlus className="h-4 w-4 mx-auto mb-1" />
            Drop crew here to assign
          </div>
        )}
      </div>
      
      {/* Drop indicator when dragging */}
      {isDragOver && (
        <div className={`mt-2 py-2 text-center rounded border-2 border-dashed ${
          canDrop 
            ? "border-teal-400 bg-teal-900/30 text-teal-300" 
            : "border-red-400 bg-red-900/30 text-red-300"
        }`}>
          <span className="text-xs font-medium">
            {canDrop ? (
              <>
                <Check className="h-3 w-3 inline mr-1" />
                Drop to assign
              </>
            ) : isAlreadyAssigned ? (
              <>
                <Ban className="h-3 w-3 inline mr-1" />
                Already assigned
              </>
            ) : (
              <>
                <Ban className="h-3 w-3 inline mr-1" />
                Not qualified
              </>
            )}
          </span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SHIFT AND PROJECT COMPONENTS FOR DND MODE
// ============================================================================

/**
 * Shift block in drag-and-drop mode
 */
function DndShiftBlock({
  shift,
  selectedTask,
  onSelectTask,
  onDropCrew,
  onRemoveCrew,
  onSendReminder,
  draggedStaff,
  dragOverTaskId,
}) {
  const [expanded, setExpanded] = useState(true);
  
  return (
    <div className="bg-surface-elevated/30 rounded-lg border border-border mb-3">
      {/* Shift Header */}
      <div
        className="flex items-center gap-2 p-3 cursor-pointer hover:bg-surface-elevated/20"
        onClick={() => setExpanded(!expanded)}
      >
        <button className="text-muted-foreground">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-white truncate">{shift.shift_name}</span>
            <Badge
              variant="outline"
              className={`text-[10px] px-1.5 py-0 ${
                shift.status === "completed"
                  ? "border-green-600 text-green-400"
                  : shift.status === "in_progress"
                  ? "border-blue-600 text-blue-400"
                  : "border-border text-muted-foreground"
              }`}
            >
              {shift.status}
            </Badge>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-0.5">
            {shift.date && (
              <span className="flex items-center gap-1">
                <Calendar className="h-2.5 w-2.5" />
                {shift.date}
              </span>
            )}
            {(shift.start_time || shift.end_time) && (
              <span className="flex items-center gap-1">
                <Clock className="h-2.5 w-2.5" />
                {shift.start_time} - {shift.end_time}
              </span>
            )}
            {shift.location_name && (
              <span className="flex items-center gap-1">
                <MapPin className="h-2.5 w-2.5" />
                {shift.location_name}
              </span>
            )}
          </div>
        </div>
        
        <CapacityIndicator
          required={shift.total_required}
          assigned={shift.total_assigned}
          accepted={shift.total_accepted}
          compact
        />
      </div>
      
      {/* Tasks */}
      {expanded && (
        <div className="p-2 pt-0 space-y-2">
          {shift.tasks.length === 0 ? (
            <p className="text-xs text-muted-foreground italic p-2">No tasks defined</p>
          ) : (
            shift.tasks.map((task) => (
              <DroppableTaskZone
                key={task.task_id}
                task={task}
                isSelected={selectedTask?.task_id === task.task_id}
                onSelect={onSelectTask}
                onDrop={onDropCrew}
                onRemoveCrew={onRemoveCrew}
                onSendReminder={onSendReminder}
                isDragOver={dragOverTaskId === task.task_id}
                draggedStaff={draggedStaff}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Project section in drag-and-drop mode
 */
function DndProjectSection({
  project,
  selectedTask,
  onSelectTask,
  onDropCrew,
  onRemoveCrew,
  onSendReminder,
  draggedStaff,
  dragOverTaskId,
}) {
  const [expanded, setExpanded] = useState(true);
  
  return (
    <div className="bg-surface-elevated/50 rounded-lg border border-border overflow-hidden mb-4">
      {/* Project Header */}
      <div
        className="flex items-center gap-3 p-3 bg-surface-elevated cursor-pointer hover:bg-surface-elevated/50"
        onClick={() => setExpanded(!expanded)}
      >
        <button className="text-muted-foreground">
          {expanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        </button>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-white truncate">{project.project_name}</span>
            {project.client_name && (
              <span className="text-xs text-muted-foreground">@ {project.client_name}</span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {project.total_shifts} shifts • {project.shifts.reduce((sum, s) => sum + s.tasks.length, 0)} tasks
          </div>
        </div>
        
        <CapacityIndicator
          required={project.total_required}
          assigned={project.total_assigned}
          accepted={project.total_accepted}
        />
      </div>
      
      {/* Shifts */}
      {expanded && (
        <div className="p-3">
          {project.shifts.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No shifts</p>
          ) : (
            project.shifts.map((shift) => (
              <DndShiftBlock
                key={shift.shift_id}
                shift={shift}
                selectedTask={selectedTask}
                onSelectTask={onSelectTask}
                onDropCrew={onDropCrew}
                onRemoveCrew={onRemoveCrew}
                onSendReminder={onSendReminder}
                draggedStaff={draggedStaff}
                dragOverTaskId={dragOverTaskId}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// REMOVAL CONFIRMATION DIALOG
// ============================================================================

function RemovalDialog({ open, onClose, crew, onConfirm }) {
  const [notifyOption, setNotifyOption] = useState("none");
  const [loading, setLoading] = useState(false);
  
  const handleConfirm = async () => {
    setLoading(true);
    await onConfirm(notifyOption === "notify");
    setLoading(false);
    onClose();
  };
  
  if (!crew) return null;
  
  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent className="bg-surface-base border-border">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">Remove Crew Assignment</AlertDialogTitle>
          <AlertDialogDescription className="text-muted-foreground">
            Remove <span className="font-medium text-white">{crew.staff_name}</span> from this task?
            {crew.status === "accepted" && (
              <span className="block mt-2 text-amber-400">
                ⚠ This crew member has already accepted this assignment.
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <div className="py-4 space-y-3">
          <label className="flex items-center gap-3 p-3 rounded border border-border hover:border-border cursor-pointer">
            <input
              type="radio"
              name="notify"
              value="none"
              checked={notifyOption === "none"}
              onChange={() => setNotifyOption("none")}
              className="text-teal-500"
            />
            <div>
              <p className="text-sm font-medium text-white">Remove without notification</p>
              <p className="text-xs text-muted-foreground">Crew will not be notified of removal</p>
            </div>
          </label>
          
          <label className="flex items-center gap-3 p-3 rounded border border-border hover:border-border cursor-pointer">
            <input
              type="radio"
              name="notify"
              value="notify"
              checked={notifyOption === "notify"}
              onChange={() => setNotifyOption("notify")}
              className="text-teal-500"
            />
            <div>
              <p className="text-sm font-medium text-white">Remove and notify crew</p>
              <p className="text-xs text-muted-foreground">Send email + PWA notification about removal</p>
            </div>
          </label>
        </div>
        
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-surface-elevated border-border text-white hover:bg-surface-elevated">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Remove"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ============================================================================
// COMPACT VIEW COMPONENTS (From PROMPT 44)
// ============================================================================

function CompactCrewMember({ crew, taskId, onRemove, onSendReminder }) {
  return (
    <div
      className="flex items-center gap-1.5 py-1 px-2 text-xs group hover:bg-surface-elevated/30 rounded"
      data-testid={`compact-crew-${crew.staff_id}`}
    >
      <StatusIndicator status={crew.status} seenAt={crew.seen_at} size="compact" />
      <User className="h-3 w-3 text-muted-foreground" />
      <span className="text-foreground truncate max-w-[150px]">{crew.staff_name}</span>
      
      <div className="hidden group-hover:flex items-center gap-1 ml-auto">
        {crew.status === "pending" && !crew.seen_at && (
          <button
            className="p-0.5 text-amber-400 hover:text-amber-300"
            onClick={(e) => {
              e.stopPropagation();
              onSendReminder?.(taskId, crew.staff_id);
            }}
            title="Send reminder"
          >
            <Mail className="h-3 w-3" />
          </button>
        )}
        <button
          className="p-0.5 text-muted-foreground hover:text-red-400"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(taskId, crew);
          }}
          title="Remove"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

function CompactTaskRow({ task, onAssignCrew, onRemoveCrew, onSendReminder, isLast }) {
  const [expanded, setExpanded] = useState(true);
  
  return (
    <div className={`${!isLast ? "border-b border-border/30" : ""}`}>
      <div
        className="flex items-center gap-2 py-1.5 px-2 hover:bg-surface-elevated/20 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <button className="text-muted-foreground hover:text-foreground">
          {expanded ? <Minus className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
        </button>
        
        <span className="text-xs font-medium text-teal-400">▸</span>
        <span className="text-sm text-foreground flex-1 truncate">{task.task_name}</span>
        
        <CapacityIndicator
          required={task.required_staff}
          assigned={task.assigned_count}
          accepted={task.accepted_count}
          compact
        />
        
        <button
          className="p-1 text-teal-400 hover:text-teal-300 hover:bg-teal-900/30 rounded"
          onClick={(e) => {
            e.stopPropagation();
            onAssignCrew(task);
          }}
          title="Assign crew"
        >
          <UserPlus className="h-3.5 w-3.5" />
        </button>
        
        {task.pending_count > 0 && (
          <button
            className="p-1 text-amber-400 hover:text-amber-300 hover:bg-amber-900/30 rounded"
            onClick={(e) => {
              e.stopPropagation();
              onSendReminder(task.task_id, null, null);
            }}
            title={`Remind ${task.pending_count} pending`}
          >
            <Mail className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      
      {expanded && task.crew.length > 0 && (
        <div className="ml-6 border-l-2 border-border/50 pl-2 pb-1">
          {task.crew.map((crew) => (
            <CompactCrewMember
              key={crew.assignment_id}
              crew={crew}
              taskId={task.task_id}
              onRemove={onRemoveCrew}
              onSendReminder={onSendReminder}
            />
          ))}
        </div>
      )}
      
      {expanded && task.crew.length === 0 && (
        <div className="ml-6 border-l-2 border-border/50 pl-2 pb-1">
          <p className="text-[10px] text-muted-foreground italic py-1 px-2">No crew assigned</p>
        </div>
      )}
    </div>
  );
}

function CompactShiftBlock({ shift, onAssignCrew, onRemoveCrew, onSendReminder }) {
  const [expanded, setExpanded] = useState(true);
  
  return (
    <div className="bg-surface-elevated/30 rounded border border-border/50 mb-2">
      <div
        className="flex items-center gap-2 p-2 hover:bg-surface-elevated/20 cursor-pointer border-b border-border/30"
        onClick={() => setExpanded(!expanded)}
      >
        <button className="text-muted-foreground hover:text-foreground">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white truncate">{shift.shift_name}</span>
            <Badge
              variant="outline"
              className={`text-[10px] px-1.5 py-0 ${
                shift.status === "completed"
                  ? "border-green-600 text-green-400"
                  : shift.status === "in_progress"
                  ? "border-blue-600 text-blue-400"
                  : "border-border text-muted-foreground"
              }`}
            >
              {shift.status}
            </Badge>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-0.5">
            {shift.date && (
              <span className="flex items-center gap-1">
                <Calendar className="h-2.5 w-2.5" />
                {shift.date}
              </span>
            )}
            {(shift.start_time || shift.end_time) && (
              <span className="flex items-center gap-1">
                <Clock className="h-2.5 w-2.5" />
                {shift.start_time} - {shift.end_time}
              </span>
            )}
            {shift.location_name && (
              <span className="flex items-center gap-1">
                <MapPin className="h-2.5 w-2.5" />
                {shift.location_name}
              </span>
            )}
          </div>
        </div>
        
        <CapacityIndicator
          required={shift.total_required}
          assigned={shift.total_assigned}
          accepted={shift.total_accepted}
          compact
        />
        
        {shift.total_pending > 0 && (
          <button
            className="p-1 text-amber-400 hover:text-amber-300 hover:bg-amber-900/30 rounded"
            onClick={(e) => {
              e.stopPropagation();
              onSendReminder(null, shift.shift_id, null);
            }}
            title={`Remind all ${shift.total_pending} pending in shift`}
          >
            <Mail className="h-4 w-4" />
          </button>
        )}
      </div>
      
      {expanded && (
        <div className="p-1">
          {shift.tasks.length === 0 ? (
            <p className="text-xs text-muted-foreground italic p-2">No tasks</p>
          ) : (
            shift.tasks.map((task, idx) => (
              <CompactTaskRow
                key={task.task_id}
                task={task}
                onAssignCrew={onAssignCrew}
                onRemoveCrew={onRemoveCrew}
                onSendReminder={onSendReminder}
                isLast={idx === shift.tasks.length - 1}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function CompactProjectSection({ project, onAssignCrew, onRemoveCrew, onSendReminder }) {
  const [expanded, setExpanded] = useState(true);
  
  return (
    <div className="bg-surface-elevated/50 rounded-lg border border-border overflow-hidden">
      <div
        className="flex items-center gap-3 p-3 bg-surface-elevated cursor-pointer hover:bg-surface-elevated/50"
        onClick={() => setExpanded(!expanded)}
      >
        <button className="text-muted-foreground hover:text-foreground">
          {expanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        </button>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-white truncate">{project.project_name}</span>
            {project.client_name && (
              <span className="text-xs text-muted-foreground">@ {project.client_name}</span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
            <span>{project.total_shifts} shifts</span>
            <span>•</span>
            <span>{project.shifts.reduce((sum, s) => sum + s.tasks.length, 0)} tasks</span>
          </div>
        </div>
        
        <CapacityIndicator
          required={project.total_required}
          assigned={project.total_assigned}
          accepted={project.total_accepted}
        />
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={(e) => e.stopPropagation()}>
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={(e) => {
              e.stopPropagation();
              onSendReminder(null, null, project.project_id);
            }}>
              <Mail className="h-4 w-4 mr-2" />
              Send Reminders (All Pending)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      {expanded && (
        <div className="p-2">
          {project.shifts.length === 0 ? (
            <p className="text-sm text-muted-foreground italic p-4">No shifts</p>
          ) : (
            project.shifts.map((shift) => (
              <CompactShiftBlock
                key={shift.shift_id}
                shift={shift}
                onAssignCrew={onAssignCrew}
                onRemoveCrew={onRemoveCrew}
                onSendReminder={onSendReminder}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// ASSIGN CREW DIALOG (For non-DnD mode)
// ============================================================================

function AssignCrewDialog({ open, onClose, task, onAssign }) {
  const [eligibleCrew, setEligibleCrew] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  
  useEffect(() => {
    if (open && task) {
      loadEligibleCrew();
      setSearchTerm("");
    }
  }, [open, task]);
  
  const loadEligibleCrew = async () => {
    setLoading(true);
    try {
      const res = await getEligibleCrew(task.task_id, true);
      const assignedIds = new Set(task.crew.map((c) => c.staff_id));
      const available = (res.data || []).filter((s) => !assignedIds.has(s.staff_id || s.id));
      setEligibleCrew(available);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  
  const handleQuickAssign = async (staffId) => {
    setAssigning(staffId);
    try {
      await assignCrewToTask(task.task_id, staffId);
      toast.success("Crew member assigned");
      setEligibleCrew((prev) => prev.filter((s) => (s.staff_id || s.id) !== staffId));
      onAssign();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setAssigning(null);
    }
  };
  
  const filteredCrew = useMemo(() => {
    if (!searchTerm) return eligibleCrew;
    const term = searchTerm.toLowerCase();
    return eligibleCrew.filter((s) =>
      `${s.first_name} ${s.last_name}`.toLowerCase().includes(term) ||
      s.email?.toLowerCase().includes(term)
    );
  }, [eligibleCrew, searchTerm]);
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Assign Crew to Task</DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            {task?.task_name}
            <CapacityIndicator
              required={task?.required_staff || 0}
              assigned={task?.assigned_count || 0}
              accepted={task?.accepted_count || 0}
              compact
            />
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : eligibleCrew.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No available crew members</p>
            <p className="text-xs mt-1">All eligible staff may already be assigned</p>
          </div>
        ) : (
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Search crew..."
              className="w-full px-3 py-2 text-sm bg-surface-elevated border border-border rounded-md text-white placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-teal-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            
            <div className="max-h-[300px] overflow-auto space-y-1">
              {filteredCrew.map((staff) => (
                <div
                  key={staff.staff_id || staff.id}
                  className="flex items-center justify-between p-2 rounded hover:bg-surface-elevated/50 group"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{staff.first_name} {staff.last_name}</p>
                      {staff.email && (
                        <p className="text-xs text-muted-foreground truncate">{staff.email}</p>
                      )}
                    </div>
                    {staff.is_eligible === false && (
                      <Badge variant="outline" className="text-xs flex-shrink-0">
                        Ineligible
                      </Badge>
                    )}
                  </div>
                  <Button
                    size="sm"
                    className="h-7 bg-teal-600 hover:bg-teal-700 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleQuickAssign(staff.staff_id || staff.id)}
                    disabled={assigning === (staff.staff_id || staff.id)}
                  >
                    {assigning === (staff.staff_id || staff.id) ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <>
                        <UserPlus className="h-3 w-3 mr-1" />
                        Assign
                      </>
                    )}
                  </Button>
                </div>
              ))}
              
              {filteredCrew.length === 0 && searchTerm && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No crew matching &ldquo;{searchTerm}&rdquo;
                </p>
              )}
            </div>
            
            <p className="text-xs text-muted-foreground">
              {filteredCrew.length} of {eligibleCrew.length} crew members shown
            </p>
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export default function AssignmentBoardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [boardData, setBoardData] = useState([]);
  const [allCrew, setAllCrew] = useState([]);
  const [loading, setLoading] = useState(true);
  const [crewLoading, setCrewLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedProject, setSelectedProject] = useState(
    searchParams.get("project") || "all"
  );
  
  // View modes: "compact", "expanded", "dnd" (drag-and-drop)
  const [viewMode, setViewMode] = useState(
    localStorage.getItem("assignmentBoardView") || "compact"
  );
  
  // DnD state
  const [selectedTask, setSelectedTask] = useState(null);
  const [filterByTask, setFilterByTask] = useState(true);
  const [draggedStaff, setDraggedStaff] = useState(null);
  const [dragOverTaskId, setDragOverTaskId] = useState(null);
  
  // Dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignTask, setAssignTask] = useState(null);
  const [removalDialogOpen, setRemovalDialogOpen] = useState(false);
  const [removalData, setRemovalData] = useState(null);
  
  // Persist view mode
  useEffect(() => {
    localStorage.setItem("assignmentBoardView", viewMode);
  }, [viewMode]);
  
  // Calculate assigned staff IDs for current board
  const assignedStaffIds = useMemo(() => {
    const ids = new Set();
    boardData.forEach((project) => {
      project.shifts.forEach((shift) => {
        shift.tasks.forEach((task) => {
          task.crew?.forEach((c) => ids.add(c.staff_id));
        });
      });
    });
    return ids;
  }, [boardData]);
  
  const loadProjects = useCallback(async () => {
    try {
      const res = await getAssignmentBoardProjects();
      setProjects(res.data || []);
    } catch (err) {
      console.error("Failed to load projects:", err);
    }
  }, []);
  
  const loadBoardData = useCallback(async () => {
    try {
      const params = {};
      if (selectedProject && selectedProject !== "all") {
        params.project_id = selectedProject;
      }
      const res = await getAssignmentBoard(params);
      setBoardData(res.data || []);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }, [selectedProject]);
  
  const loadAllCrew = useCallback(async () => {
    setCrewLoading(true);
    try {
      const res = await getStaffList({ status: "active", limit: 1000 });
      // Handle paginated response format
      const crewData = res.data;
      setAllCrew(crewData?.items || crewData || []);
    } catch (err) {
      console.error("Failed to load crew:", err);
    } finally {
      setCrewLoading(false);
    }
  }, []);
  
  const refreshData = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([loadBoardData(), loadAllCrew()]);
      toast.success("Board refreshed");
    } finally {
      setRefreshing(false);
    }
  }, [loadBoardData, loadAllCrew]);
  
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadProjects(), loadBoardData(), loadAllCrew()]);
      setLoading(false);
    };
    init();
  }, [loadProjects, loadBoardData, loadAllCrew]);
  
  useEffect(() => {
    if (selectedProject !== "all") {
      setSearchParams({ project: selectedProject });
    } else {
      setSearchParams({});
    }
  }, [selectedProject, setSearchParams]);
  
  const handleProjectChange = (value) => {
    setSelectedProject(value);
  };
  
  const handleSelectTask = (task) => {
    setSelectedTask(selectedTask?.task_id === task.task_id ? null : task);
  };
  
  const handleAssignCrew = (task) => {
    setAssignTask(task);
    setAssignDialogOpen(true);
  };
  
  const handleDropCrew = async (task, staff) => {
    try {
      await assignCrewToTask(task.task_id, staff.id);
      toast.success(`${staff.first_name} ${staff.last_name} assigned to ${task.task_name}`);
      await loadBoardData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };
  
  const handleRemoveCrew = async (taskId, crew) => {
    setRemovalData({ taskId, crew });
    setRemovalDialogOpen(true);
  };
  
  const handleConfirmRemoval = async (notify) => {
    if (!removalData) return;
    
    try {
      await removeCrewFromTask(removalData.taskId, removalData.crew.staff_id);
      
      if (notify) {
        // TODO: Implement notification API call
        toast.success("Crew removed and notified");
      } else {
        toast.success("Crew removed");
      }
      
      await loadBoardData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };
  
  const handleSendReminder = async (taskId, shiftId, projectId) => {
    const scope = taskId ? "task" : shiftId ? "shift" : "project";
    
    if (!window.confirm(`Send reminder emails to all pending crew in this ${scope}?`)) {
      return;
    }
    
    try {
      const params = {};
      if (taskId) params.shift_task_id = taskId;
      if (shiftId) params.shift_id = shiftId;
      if (projectId) params.project_id = projectId;
      
      const res = await sendAssignmentReminder(params);
      const data = res.data;
      
      if (data.sent_count > 0) {
        toast.success(data.message);
      } else {
        toast.info(data.message || "No pending assignments to remind");
      }
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };
  
  // Calculate totals
  const totalStats = boardData.reduce(
    (acc, project) => ({
      projects: acc.projects + 1,
      shifts: acc.shifts + project.total_shifts,
      tasks: acc.tasks + project.shifts.reduce((s, shift) => s + shift.tasks.length, 0),
      required: acc.required + project.total_required,
      assigned: acc.assigned + project.total_assigned,
      accepted: acc.accepted + project.total_accepted,
    }),
    { projects: 0, shifts: 0, tasks: 0, required: 0, assigned: 0, accepted: 0 }
  );
  
  const understaffedCount = boardData.reduce((acc, project) => {
    return acc + project.shifts.reduce((shiftAcc, shift) => {
      return shiftAcc + shift.tasks.filter((t) => t.assigned_count < t.required_staff).length;
    }, 0);
  }, 0);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="assignment-board-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-bold">Assignment Board</h1>
          <p className="text-muted-foreground text-sm">
            Manage crew assignments across projects, shifts, and tasks
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex items-center bg-surface-elevated rounded-lg p-1 border border-border">
            <button
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                viewMode === "compact"
                  ? "bg-teal-600 text-white"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setViewMode("compact")}
              title="Compact view"
            >
              <LayoutList className="h-3.5 w-3.5" />
              Compact
            </button>
            <button
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                viewMode === "dnd"
                  ? "bg-teal-600 text-white"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setViewMode("dnd")}
              title="Drag & Drop assignment mode"
            >
              <Hand className="h-3.5 w-3.5" />
              Assign Mode
            </button>
          </div>
          
          <Select value={selectedProject} onValueChange={handleProjectChange}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            size="sm"
            onClick={refreshData}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>
      
      {/* Summary Stats */}
      <div className="grid grid-cols-3 md:grid-cols-7 gap-2 mb-4">
        <Card className="bg-surface-elevated/50 border-border">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-muted-foreground uppercase">Projects</p>
            <p className="text-xl font-bold text-white">{totalStats.projects}</p>
          </CardContent>
        </Card>
        <Card className="bg-surface-elevated/50 border-border">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-muted-foreground uppercase">Shifts</p>
            <p className="text-xl font-bold text-white">{totalStats.shifts}</p>
          </CardContent>
        </Card>
        <Card className="bg-surface-elevated/50 border-border">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-muted-foreground uppercase">Tasks</p>
            <p className="text-xl font-bold text-white">{totalStats.tasks}</p>
          </CardContent>
        </Card>
        <Card className="bg-surface-elevated/50 border-border">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-muted-foreground uppercase">Required</p>
            <p className="text-xl font-bold text-white">{totalStats.required}</p>
          </CardContent>
        </Card>
        <Card className="bg-surface-elevated/50 border-border">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-muted-foreground uppercase">Assigned</p>
            <p className="text-xl font-bold text-white">{totalStats.assigned}</p>
          </CardContent>
        </Card>
        <Card className="bg-surface-elevated/50 border-border">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-muted-foreground uppercase">Accepted</p>
            <p className="text-xl font-bold text-green-400">{totalStats.accepted}</p>
          </CardContent>
        </Card>
        <Card className={`border ${understaffedCount > 0 ? "bg-red-900/20 border-red-700/50" : "bg-surface-elevated/50 border-border"}`}>
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-muted-foreground uppercase">Understaffed</p>
            <p className={`text-xl font-bold ${understaffedCount > 0 ? "text-red-400" : "text-green-400"}`}>
              {understaffedCount}
            </p>
          </CardContent>
        </Card>
      </div>
      
      {/* Status Legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground bg-surface-elevated/30 px-3 py-2 rounded-lg border border-border/50 mb-4">
        <span className="font-medium text-foreground">Legend:</span>
        <span className="flex items-center gap-1">
          <StatusIndicator status="pending" seenAt={null} size="compact" />
          Sent
        </span>
        <span className="flex items-center gap-1">
          <StatusIndicator status="pending" seenAt={new Date()} size="compact" />
          Seen
        </span>
        <span className="flex items-center gap-1">
          <StatusIndicator status="accepted" size="compact" />
          Accepted
        </span>
        <span className="flex items-center gap-1">
          <StatusIndicator status="rejected" size="compact" />
          Rejected
        </span>
        <span className="text-muted-foreground">|</span>
        <span className="flex items-center gap-1">
          <span className="text-xs">5/8</span>
          <span className="text-muted-foreground">= Assigned/Required</span>
        </span>
        <span className="flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 text-red-400" />
          Understaffed
        </span>
      </div>
      
      {/* Main Content */}
      {boardData.length === 0 ? (
        <Card className="bg-surface-elevated/50 border-border flex-1">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              No Projects Found
            </h3>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              {selectedProject !== "all"
                ? "The selected project has no active shifts or data."
                : "Create a project and add shifts to start managing crew assignments."}
            </p>
          </CardContent>
        </Card>
      ) : viewMode === "dnd" ? (
        /* Drag & Drop Mode - Split Pane Layout */
        <div className="flex-1 flex border border-border rounded-lg overflow-hidden bg-surface-base/30">
          {/* Crew Pool (Left) */}
          <CrewPoolPanel
            allCrew={allCrew}
            assignedStaffIds={assignedStaffIds}
            selectedTask={selectedTask}
            filterByTask={filterByTask}
            setFilterByTask={setFilterByTask}
            loading={crewLoading}
            onDragStart={(staff) => setDraggedStaff(staff)}
          />
          
          {/* Board (Right) */}
          <div className="flex-1 overflow-y-auto p-4">
            {selectedTask && (
              <div className="mb-4 p-3 bg-teal-900/20 border border-teal-700/50 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-teal-400" />
                  <span className="text-sm text-teal-300">
                    Task selected: <span className="font-medium text-white">{selectedTask.task_name}</span>
                  </span>
                  {selectedTask.required_skills?.length > 0 && (
                    <div className="flex gap-1 ml-2">
                      {selectedTask.required_skills.map((skill) => (
                        <Badge key={skill} className="text-[9px] bg-teal-700 text-white">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => setSelectedTask(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
            
            <div className="space-y-4">
              {boardData.map((project) => (
                <DndProjectSection
                  key={project.project_id}
                  project={project}
                  selectedTask={selectedTask}
                  onSelectTask={handleSelectTask}
                  onDropCrew={handleDropCrew}
                  onRemoveCrew={handleRemoveCrew}
                  onSendReminder={handleSendReminder}
                  draggedStaff={draggedStaff}
                  dragOverTaskId={dragOverTaskId}
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        /* Compact View */
        <div className="flex-1 overflow-y-auto space-y-3">
          {boardData.map((project) => (
            <CompactProjectSection
              key={project.project_id}
              project={project}
              onAssignCrew={handleAssignCrew}
              onRemoveCrew={handleRemoveCrew}
              onSendReminder={handleSendReminder}
            />
          ))}
        </div>
      )}
      
      {/* Dialogs */}
      <AssignCrewDialog
        open={assignDialogOpen}
        onClose={() => {
          setAssignDialogOpen(false);
          setAssignTask(null);
        }}
        task={assignTask}
        onAssign={loadBoardData}
      />
      
      <RemovalDialog
        open={removalDialogOpen}
        onClose={() => {
          setRemovalDialogOpen(false);
          setRemovalData(null);
        }}
        crew={removalData?.crew}
        onConfirm={handleConfirmRemoval}
      />
    </div>
  );
}
